import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Form, Typography, Card, Button, Switch, Spin, notification,
} from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import './NotificationSettings.scss';
import {
  get, put, isSuperAgentError, RequestWrapper, ResponseWrapper,
} from '../../dataSource/requests';

const NotificationSettings = (): React.ReactElement => {
  interface CustomCategory {
    name: string;
    notification: boolean;
    notificationCategoryId: number;
  }

  interface FixedCategory {
    title: string;
    name: string;
    value: boolean;
  }

  interface SettingsForm {
    fixed: {
      // notificationFinancial: boolean;
      notificationService: boolean;
    },
    custom: {
      [categoryID: string]: boolean;
    }
  }

  const navigate = useNavigate();
  const [formInstance] = Form.useForm();

  const [loading, setLoading] = useState<boolean>(false);

  const [fixedCategories, setFixedCategories] = useState<Array<FixedCategory>>([
    // {
    //   title: 'Financeiro',
    //   name: 'notificationFinancial',
    //   value: false,
    // },
    {
      title: 'Serviços',
      name: 'notificationService',
      value: false,
    },
  ]);

  const [customCategories, setCustomCategories] = useState<Array<CustomCategory>>([]);

  const fetchNotificationsPreferences = async (): Promise<void> => {
    setLoading(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = get({
      url: '/notification/getNotificationsPreferences',

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 7000):
        // "Notification count retrieved by user successfully", 7000
        setFixedCategories(
          [
            // {
            //   title: 'Financeiro',
            //   name: 'notificationFinancial',
            //   value: response?.body?.unreadNotificationsByUser?.notificationFinancial,
            // },
            {
              title: 'Serviços',
              name: 'notificationService',
              value: response?.body?.unreadNotificationsByUser?.notificationService,
            },
          ],
        );

        setCustomCategories(
          response?.body?.unreadNotificationsByUser?.notificationCategoryUserOutputList,
        );

        formInstance.setFieldsValue({
          fixed: {
            // notificationFinancial:
            //   response?.body?.unreadNotificationsByUser?.notificationFinancial,
            notificationService:
              response?.body?.unreadNotificationsByUser?.notificationService,
          },

          custom: Object.fromEntries(
            response
              ?.body
              ?.unreadNotificationsByUser
              ?.notificationCategoryUserOutputList
              ?.map((element: CustomCategory) => {
                return [`category#%#${element.notificationCategoryId}`, element.notification];
              }),
          ),
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao buscar as preferencias de notificação. Tente novamente mais tarde.',
        });
        break;
    }

    setLoading(false);
  };

  useEffect(() => {
    const executeOnMount = async (): Promise<void> => {
      await fetchNotificationsPreferences();
    };

    executeOnMount();

    return () => {
      setLoading(false);
    };
  }, []);

  const handleSuccessfulSubmit = async (values: SettingsForm): Promise<void> => {
    setLoading(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const finalCustomCategoryArray = Object.entries(values.custom).map((elementCategory) => {
      const splitIndex = elementCategory[0].split('#%#');
      let id: number | undefined;

      if (splitIndex?.[1]) {
        id = Number.parseInt(splitIndex[1], 10);
      }

      if (id == null) {
        return null;
      }

      const foundCategory = customCategories.find((originalCategory) => {
        return originalCategory.notificationCategoryId === id;
      });

      if (foundCategory == null) {
        return null;
      }

      return ({
        name: foundCategory?.name,
        notification: elementCategory[1],
        notificationCategoryId: id,
      });
    }).filter((el) => {
      return el != null;
    });

    const finalParams = {
      notificationCategoryUserOutputList: finalCustomCategoryArray,
      // notificationFinancial: values.fixed.notificationFinancial,
      notificationService: values.fixed.notificationService,
    };

    const requestReference = put({
      url: '/notification/editNotificationsPreferences',

      frontConfigs: {
        navigateFunction: navigate,
      },

      params: finalParams,
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 7005):
        // "User notification preference changed successfully", 7005
        notification.success({
          message: 'Sucesso',
          description: 'Preferências de notificações atualizadas.',
        });

        navigate('/home');
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao atualizar as preferências de notificações.',
        });
        break;
    }

    setLoading(false);
  };

  return (
    <div
      className="NotificationSettings"
    >
      <Row
        gutter={[24, 24]}
        className="NotificationSettings__AllContentRow"
      >
        <Col
          span={24}
          className="NotificationSettings__AllContentColumn"
        >
          <Form
            layout="vertical"
            form={formInstance}
            className="NotificationSettings__Form"
            onFinish={handleSuccessfulSubmit}
            onFinishFailed={
              (errorInfo): void => {
                console.log('NotificationSettings form submit failed', errorInfo);
              }
            }
          >
            <Row
              gutter={[24, 24]}
              className="NotificationSettings__HeaderRow"
            >
              <Col
                xs={{ span: 24 }}
                md={{ span: 12 }}
                lg={{ span: 16 }}
                className="NotificationSettings__TitleColumn"
              >
                <Typography.Title
                  className="NotificationSettings__PageTitle"
                >
                  Configurar notificações
                </Typography.Title>
              </Col>

              <Col
                xs={{ span: 12 }}
                md={{ span: 6 }}
                lg={{ span: 4 }}
                className="NotificationSettings__BackButtonColumn"
              >
                <Form.Item
                  className="NotificationSettings__BackButtonFormItem"
                >
                  <Button
                    type="default"
                    htmlType="button"
                    onClick={
                      (): void => {
                        navigate('/home');
                      }
                    }
                    className="NotificationSettings__BackButton"
                    block
                  >
                    Voltar
                  </Button>
                </Form.Item>
              </Col>

              <Col
                xs={{ span: 12 }}
                md={{ span: 6 }}
                lg={{ span: 4 }}
                className="NotificationSettings__SubmitButtonColumn"
              >
                <Form.Item
                  className="NotificationSettings__SubmitButtonFormItem"
                >
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    className="NotificationSettings__SubmitButton"
                  >
                    Salvar
                  </Button>
                </Form.Item>
              </Col>
            </Row>

            <Row
              gutter={[24, 24]}
              className="NotificationSettings__CardParentRow"
            >
              <Col
                span={24}
                className="NotificationSettings__CardParentColumn"
              >
                <Card
                  className="NotificationSettings__Card"
                  title={
                    (
                      <Typography.Text>
                        <InfoCircleFilled className="NotificationSettings__CardTitleIcon" />
                        Configuração válida para notificações por push
                      </Typography.Text>
                    )
                  }
                >
                  <Spin
                    className="NotificationSettings__Spin"
                    spinning={loading}
                  >
                    <Row>
                      <Col
                        xs={{ span: 24 }}
                      >
                        <Typography.Title
                          level={5}
                          className="NotificationSettings__FixedCategoryTitle"
                        >
                          Categorias fixas de notificação
                        </Typography.Title>
                      </Col>
                    </Row>

                    <Row
                      gutter={[24, 24]}
                      className="NotificationSettings__CatogoriesRow"
                    >
                      {
                        fixedCategories.map((element) => {
                          return (
                            <Col
                              key={`column-category-${element.name}`}
                              xs={{ span: 24 }}
                              sm={{ span: 12 }}
                              md={{ span: 12 }}
                              lg={{ span: 12 }}
                              className="NotificationSettings__CategoryColumn"
                            >
                              <Form.Item
                                colon={false}
                                label={element.title}
                                name={['fixed', element.name]}
                                valuePropName="checked"
                                className="NotificationSettings__CategoryFormItem"
                                initialValue={element.value}
                              >
                                <Switch
                                  title={element.title}
                                  className="NotificationSettings__CategorySwitch"
                                  unCheckedChildren="Inativo"
                                  checkedChildren="Ativo"
                                />
                              </Form.Item>
                            </Col>
                          );
                        })
                      }
                    </Row>

                    <Row className="NotificationSettings__CustomCategoryRow">
                      <Col
                        xs={{ span: 24 }}
                      >
                        <Typography.Title
                          level={5}
                          className="NotificationSettings__CustomCategoryTitle"
                        >
                          Categorias customizadas de notificação
                        </Typography.Title>
                      </Col>
                    </Row>

                    <Row>
                      {
                        customCategories?.map((element) => {
                          return (
                            <Col
                              key={`column-category-${element.name}`}
                              xs={{ span: 24 }}
                              sm={{ span: 12 }}
                              md={{ span: 12 }}
                              lg={{ span: 12 }}
                              className="NotificationSettings__CategoryColumn"
                            >
                              <Form.Item
                                noStyle
                              >
                                <Form.Item
                                  label={element.name}
                                  labelAlign="left"
                                  colon={false}
                                  name={['custom', `category#%#${element.notificationCategoryId}`]}
                                  valuePropName="checked"
                                  className="NotificationSettings__CategoryFormItem"
                                  initialValue={element.notification}
                                >
                                  <Switch
                                    title={element.name}
                                    className="NotificationSettings__CategorySwitch"
                                    unCheckedChildren="Inativo"
                                    checkedChildren="Ativo"
                                  />
                                </Form.Item>
                              </Form.Item>
                            </Col>
                          );
                        })
                      }
                    </Row>
                  </Spin>
                </Card>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

NotificationSettings.propTypes = {};

NotificationSettings.defaultProps = {};

export default NotificationSettings;
